import { CommitPayload, GetPolicyResponse, PolicyDetails, UpdateModel } from "@/models/policy";
import { policyService } from "@/services/policyService";
import { useMemo, useState } from "react";
import Input from "@/components/common/input/Input";
import Breadcrumb from "@/components/bread-crumb/BreadCrumb";
import "./PolicyStatusUpdateUtility.css";

const PolicyStatusUpdateUtility = () => {
  const [policyId, setPolicyId] = useState("");
  const [policyTxnNum, setPolicyTxnNum] = useState("");

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [details, setDetails] = useState<PolicyDetails | null>(null);

  const [searchError, setSearchError] = useState<string | null>(null);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [commitNotice, setCommitNotice] = useState<string | null>(null);

  const [updateDraft, setUpdateDraft] = useState<UpdateModel>({
    txnStatusCode: "",
    processStatusCode: "",
    quoteStatus: ""
  });

  const [changePrepared, setChangePrepared] = useState(false);
  const [committing, setCommitting] = useState(false);

  const [undoSnapshot, setUndoSnapshot] = useState<PolicyDetails | null>(null);
  const [undoEnabled, setUndoEnabled] = useState(false);

  const userId = useMemo(() => {
    const v = (sessionStorage.getItem("userId") ?? "").trim();
    return v.length > 0 ? v : "hcch//system";
  }, []);

  const canViewDetails = useMemo(() => {
    return policyId.trim().length > 0 && policyTxnNum.trim().length > 0;
  }, [policyId, policyTxnNum]);

  const canPrepareChange = useMemo(() => {
    if (!details) return false;

    const nextTxn = updateDraft.txnStatusCode.trim();
    const nextProc = updateDraft.processStatusCode.trim();
    const nextQuote = updateDraft.quoteStatus.trim();

    const anyTyped = nextTxn.length > 0 || nextProc.length > 0 || nextQuote.length > 0;
    if (!anyTyped) return false;

    const differs =
      (nextTxn.length > 0 && nextTxn !== (details.txnStatusCode ?? "").trim()) ||
      (nextProc.length > 0 && nextProc !== (details.processStatusCode ?? "").trim()) ||
      (nextQuote.length > 0 && nextQuote !== (details.quoteStatus ?? "").trim());

    return differs;
  }, [details, updateDraft]);

  const canCommit = useMemo(() => {
    return !!details && changePrepared && !committing;
  }, [details, changePrepared, committing]);

  async function handleViewDetails() {
    setLoadingDetails(true);
    setSearchError(null);
    setUpdateError(null);
    setCommitNotice(null);

    setDetails(null);
    setChangePrepared(false);
    setUpdateDraft({ txnStatusCode: "", processStatusCode: "", quoteStatus: "" });
    setUndoSnapshot(null);
    setUndoEnabled(false);

    try {
      const pid = policyId.trim();
      const txn = policyTxnNum.trim();

      const res = await policyService.getPolicyTxnDetails(pid, txn);

      if (res?.status === 204) {
        const msg = (res as any)?.data?.message || (res as any)?.data || "No record found for that Policy ID / Txn Num.";
        setSearchError(typeof msg === "string" ? msg : "No record found for that Policy ID / Txn Num.");
        setDetails(null);
        return;
      }

      const { data } = res;
      const typed = data as GetPolicyResponse;
      const quoteStatus = typed?.quotes?.[0]?.quoteStatus ?? "";

      const mapped: PolicyDetails = {
        policyId: String(typed.policyId ?? pid),
        policyTxnNum: String(typed.policyTxnNum ?? txn),
        txnStatusCode: typed.txnStatusCode ?? "",
        processStatusCode: typed.processStatusCode ?? "",
        approveQuoteCode:
          typed.approvedQuoteId !== undefined && typed.approvedQuoteId !== null
            ? String(typed.approvedQuoteId)
            : "",
        transactionType: typed.txnTypeCode ?? "",
        insuredName: typed.insuredFullName ?? "",
        quoteStatus
      };

      if (!mapped.policyId || !mapped.policyTxnNum) {
        setSearchError("No record found for that Policy ID / Txn Num.");
        return;
      }

      setDetails(mapped);
      setUpdateDraft({ txnStatusCode: "", processStatusCode: "", quoteStatus: "" });
    } catch (err: any) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.response?.data ||
        err?.message ||
        "API call failed";

      if (status === 204 || status === 400) {
        setSearchError(typeof msg === "string" ? msg : "No record found for that Policy ID / Txn Num.");
      } else {
        setSearchError(typeof msg === "string" ? msg : "API call failed");
      }

      setDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  }

  function handlePrepareChange() {
    setChangePrepared(true);
    setCommitNotice(null);
    setSearchError(null);
    setUpdateError(null);
  }

  async function handleCommit() {
    if (!details) return;

    setCommitting(true);
    setUpdateError(null);
    setCommitNotice(null);

    if (!changePrepared) {
      setUpdateError("Please click Change Quote Status before Commit.");
      setCommitting(false);
      return;
    }

    try {
      const effectiveTxn =
        updateDraft.txnStatusCode.trim().length > 0 ? updateDraft.txnStatusCode.trim() : (details.txnStatusCode ?? "").trim();

      const effectiveProc =
        updateDraft.processStatusCode.trim().length > 0
          ? updateDraft.processStatusCode.trim()
          : (details.processStatusCode ?? "").trim();

      const effectiveQuote =
        updateDraft.quoteStatus.trim().length > 0 ? updateDraft.quoteStatus.trim() : (details.quoteStatus ?? "").trim();

      const snapshotBeforeCommit: PolicyDetails = { ...details };
      setUndoSnapshot(snapshotBeforeCommit);

      const payload: CommitPayload = {
        policyID: Number(details.policyId),
        policyTxnNum: Number(details.policyTxnNum),
        processStatusCode: effectiveProc,
        txnStatusCode: effectiveTxn,
        quoteStatus: effectiveQuote,
        userId
      };

      const res = await policyService.commitPolicyStatus(payload);

      if (res?.status >= 200 && res?.status < 300) {
        setCommitNotice("Changes committed");

        const updated: PolicyDetails = {
          ...details,
          txnStatusCode: effectiveTxn,
          processStatusCode: effectiveProc,
          quoteStatus: effectiveQuote
        };

        setDetails(updated);
        setUpdateDraft({ txnStatusCode: "", processStatusCode: "", quoteStatus: "" });
        setChangePrepared(false);
        setUndoEnabled(true);
      } else {
        const msg = (res as any)?.data?.message || (res as any)?.data?.errorMessage || (res as any)?.data || "Commit failed";
        setUpdateError(typeof msg === "string" ? msg : "Commit failed");
        setUndoSnapshot(null);
        setUndoEnabled(false);
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.response?.data ||
        err?.message ||
        "Commit failed";
      setUpdateError(typeof msg === "string" ? msg : "Commit failed");
      setUndoSnapshot(null);
      setUndoEnabled(false);
    } finally {
      setCommitting(false);
    }
  }

  async function handleUndo() {
    if (!details || !undoEnabled || !undoSnapshot) return;

    setCommitting(true);
    setUpdateError(null);
    setCommitNotice(null);

    try {
      const payload: CommitPayload = {
        policyID: Number(details.policyId),
        policyTxnNum: Number(details.policyTxnNum),
        processStatusCode: (undoSnapshot.processStatusCode ?? "").trim(),
        txnStatusCode: (undoSnapshot.txnStatusCode ?? "").trim(),
        quoteStatus: (undoSnapshot.quoteStatus ?? "").trim(),
        userId
      };

      const res = await policyService.commitPolicyStatus(payload);

      if (res?.status >= 200 && res?.status < 300) {
        setCommitNotice("Changes reverted");
        setDetails(undoSnapshot);
        setUndoSnapshot(null);
        setUndoEnabled(false);
        setUpdateDraft({ txnStatusCode: "", processStatusCode: "", quoteStatus: "" });
        setChangePrepared(false);
      } else {
        const msg = (res as any)?.data?.message || (res as any)?.data?.errorMessage || (res as any)?.data || "Undo failed";
        setUpdateError(typeof msg === "string" ? msg : "Undo failed");
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errorMessage ||
        err?.response?.data ||
        err?.message ||
        "Undo failed";
      setUpdateError(typeof msg === "string" ? msg : "Undo failed");
    } finally {
      setCommitting(false);
    }
  }

  const undoButtonDisabled = !details || committing || !undoEnabled;
  const undoButtonText = "Undo Change";

  return (
    <>
      <div className="page-container">
        <div className="card-container">
          <div className="top-row">
            <div className="breadcrumb-area">
              <Breadcrumb
                items={[
                  { label: "Home", path: "/" },
                  { label: "Policy Status Update", path: "/policy-status-update" }
                ]}
              />
            </div>
            <a
              id="ContentPlaceHolderCenter_HyperLinkHelp"
              href="http://ustxhouinfappd1/PolicyStatusUpdate/Resources/PolicyStatusUpdate.doc"
              className="breadcrumb-help-link"
            >
              Help
            </a>
          </div>
          <div className="card-section">
            <h3 className="section-title">Search Policy Transaction</h3>

            <div className="form-grid">
              <div className="form-group">
                <label>Policy ID</label>
                <Input
                  className="pn-is-input"
                  placeholder="Enter policy id"
                  value={policyId}
                  onChange={e => {
                    setPolicyId(e.target.value.replace(/\D/g, ""));
                    setSearchError(null);
                    setUpdateError(null);
                    setCommitNotice(null);
                  }}
                />
              </div>

              <div className="form-group">
                <label>Policy Txn Num</label>
                <Input
                  className="pn-is-input"
                  placeholder="Enter txn number"
                  value={policyTxnNum}
                  onChange={e => {
                    setPolicyTxnNum(e.target.value.replace(/\D/g, ""));
                    setSearchError(null);
                    setUpdateError(null);
                    setCommitNotice(null);
                  }}
                />
              </div>
            </div>

            <div className="action-row left">
              <button className="btn primary" disabled={!canViewDetails || loadingDetails} onClick={handleViewDetails}>
                View Details
              </button>
            </div>

            {searchError ? <div className="err-text">{searchError}</div> : null}
          </div>

          {details ? (
            <>
              <div className="card-section light">
                <div className="details-card">
                  <div className="detail-row">
                    <span>Policy ID:</span>
                    <strong className="strong-value">{details.policyId}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Policy Txn Num:</span>
                    <strong className="strong-value">{details.policyTxnNum}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Txn Status Code:</span>
                    <strong className="strong-value">{details.txnStatusCode}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Process Status Code:</span>
                    <strong className="strong-value">{details.processStatusCode ? <>{details.processStatusCode}</> : undefined}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Approve Quote Code:</span>
                    <strong className="strong-value">{details.approveQuoteCode}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Transaction Type:</span>
                    <strong className="strong-value">{details.transactionType}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Insured Name:</span>
                    <strong className="strong-value">{details.insuredName}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Quote Status:</span>
                    <strong className="strong-value">{details.quoteStatus ? <>{details.quoteStatus}</> : undefined}</strong>
                  </div>
                </div>
              </div>

              <div className="card-section">
                <h3 className="section-title">Update Policy</h3>

                <div className="form-grid single">
                  <div className="form-group">
                    <label>Txn Status Code</label>
                    <Input
                      className="pn-is-input"
                      placeholder={`Current: ${details.txnStatusCode}`}
                      value={updateDraft.txnStatusCode}
                      onChange={e => {
                        setUpdateDraft(p => ({ ...p, txnStatusCode: e.target.value }));
                        setUpdateError(null);
                        setCommitNotice(null);
                        setChangePrepared(false);
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Process Status Code</label>
                    <Input
                      className="pn-is-input"
                      placeholder={`Current: ${details.processStatusCode}`}
                      value={updateDraft.processStatusCode}
                      onChange={e => {
                        setUpdateDraft(p => ({ ...p, processStatusCode: e.target.value }));
                        setUpdateError(null);
                        setCommitNotice(null);
                        setChangePrepared(false);
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Quote Status</label>
                    <Input
                      className="pn-is-input"
                      placeholder={`Current: ${details.quoteStatus}`}
                      value={updateDraft.quoteStatus}
                      onChange={e => {
                        setUpdateDraft(p => ({ ...p, quoteStatus: e.target.value }));
                        setUpdateError(null);
                        setCommitNotice(null);
                        setChangePrepared(false);
                      }}
                    />
                  </div>
                </div>

                <div className="action-row">
                  <button className="btn primary" disabled={!canPrepareChange} onClick={handlePrepareChange}>
                    Change Quote Status
                  </button>
                  <button className="btn secondary" disabled={!canCommit} onClick={handleCommit}>
                    {committing ? "Committing..." : "Commit"}
                  </button>
                  <button className="btn tertiary" disabled={undoButtonDisabled} onClick={handleUndo}>
                    {undoButtonText}
                  </button>
                </div>

                {updateError ? <div className="err-text">{updateError}</div> : null}

                {commitNotice ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      background: "#fff3cd",
                      border: "1px solid #ffeeba",
                      color: "#856404",
                      borderRadius: 6,
                      fontWeight: 600
                    }}
                  >
                    {commitNotice}
                  </div>
                ) : null}

                <div className="warning-text">⚠️ You need to click <strong>Commit</strong> button for the change to take place!</div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PolicyStatusUpdateUtility;
